import { useTable } from "react-table";
import { useMemo, useState, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-theme-material.css";
import { ColDef } from "ag-grid-community";
import { IProgram } from "@pbm/programs/contracts";
import { useParams } from "react-router-dom";
import { IGraphQLError } from "@common/contracts";
import {
  GridDropPaginationType,
  GridDropZoneProps,
  IDropFilteredColumn,
} from "./GridDropZone.types";
import { ptBR } from "date-fns/locale";
import { Button, BreadCrumb, Title } from "@funcional/react-ui";
import { Private, SsoRolesEnum } from "@funcional/react-auth-ui";
import { usePrograms, useProgramByPriority } from "@pbm/programs/hooks";
import { format } from "date-fns";

function normalizeData<T>(
  data: any,
  paginationType: GridDropPaginationType
): T[] {
  if (paginationType === "cursor") {
    return (data?.edges || []).map((edge: any) => edge.node);
  }
  return data?.data || [];
}

export function GridViewDropZone<T>({
  hook,
  columns,
  defaultFilters,
  paginationType = "cursor",
}: GridDropZoneProps<T>) {
  const [filters, setFilters] = useState<IDropFilteredColumn[]>(
    defaultFilters || []
  );

  const { data, isFetching } = hook({
    gridFilters: filters,
  });

  const tableInstance = useTable({
    columns,
    data: normalizeData<T>(data, paginationType || "cursor"),
    manualFilters: true,
  } as any);

  const [tabledata, setTabledata] = useState<IProgram[]>([]);
  const [items, seItitens] = useState<IProgram[]>([]);

  useEffect(() => {
    seItitens(tableInstance.data as IProgram[]);
  }, [tableInstance, isFetching]);

  const DateRenderer = ({ value }) => {
    const formattedDate = format(
      new Date(value),
      "dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss",
      {
        locale: ptBR,
      }
    );
    return <span>{formattedDate}</span>;
  };

  const cellStyles = {
    fontSize: "14px",
    textAlign: "left",
    padding: "30px",
    lineHeight: "40px",
    fontFamily: "Roboto, Helvica, Arial, sans-serif",
  };

  const [columnDefs] = useState<ColDef[]>([
    {
      field: "priority",
      headerName: "PRIORIDADE",
      rowDrag: true,
      cellStyle: {
        color: "#5B9911",
        ...cellStyles,
      },
    },
    {
      field: "code",
      headerName: "CÓDIGO",
      cellStyle: {
        color: "#6b7280",
        ...cellStyles,
      },
    },
    {
      field: "name",
      headerName: "NOME",
      cellStyle: {
        color: "#6b7280",
        ...cellStyles,
      },
    },
    {
      field: "effectiveSince",
      headerName: "INÍCIO DA VIGÊNCIA",
      cellRenderer: DateRenderer,
      cellStyle: {
        color: "#6b7280",
        ...cellStyles,
      },
    },
    {
      field: "effectiveUntil",
      headerName: "FIM DA VIGÊNCIA",
      cellRenderer: DateRenderer,
      cellStyle: {
        color: "#6b7280",
        ...cellStyles,
      },
    },
  ]);
  const getRowHeight = (params) => {
    return 100;
  };

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      enableRowGroup: false,
      enablePivot: false,
      enableValue: false,
      sortable: false,
      resizable: false,
      flex: 1,
      minWidth: 100,
    };
  }, []);

  const { establishmentIdentity }: { establishmentIdentity: string } =
    useParams();

  usePrograms({
    filter: {
      establishmentIdentity: establishmentIdentity,
    },
  });

  function createRowData() {
    let dataRow = items
      .filter((item) => item.establishmentIdentity === establishmentIdentity)
      .map((item) => item)
      .sort((a, b) => {
        if (a.priority === undefined && b.priority === undefined) {
          return 0;
        }
        if (a.priority === undefined) {
          return 1;
        }
        if (b.priority === undefined) {
          return -1;
        }
        return (a.priority || 0) - (b.priority || 0);
      });

    return dataRow;
  }

  const onRowDragEnd = (event) => {
    const gridApi = event.api;
    const tableProgramPriority: IProgram[] = [];

    gridApi.forEachNode((rowNode, index) => {
      rowNode = rowNode.data;
      rowNode.priority = index + 1;
      tableProgramPriority.push(rowNode);
    });

    setTabledata(tableProgramPriority);
  };
  useEffect(() => {
    setFilters((tableInstance.state as any).filters);
  }, [tableInstance.state, setFilters]);

  const { isLoading, isSuccess, mutateAsync } = useProgramByPriority();
  const [graphQLErrors, setGraphQLErrors] = useState<IGraphQLError[]>([]);

  const onSubmit = async () => {
    const prioridades = {
      establishmentCode: establishmentIdentity,
      priority: tabledata?.map((item: any) => ({
        id: item.priority,
        programCode: item.code,
      })),
    };

    try {
      await mutateAsync(Object.assign(prioridades));
    } catch (error: any) {
      console.error(error);
      if (error.response.errors) {
        return setGraphQLErrors(error.response.errors || []);
      }
      setGraphQLErrors([
        {
          message:
            "Não foi possível atualizar os registros de prioridade, tente novamente mais tarde.",
        },
      ]);
    }
  };

  return (
    <>
      <div className="flex items-center w-full mb-4">
        <div className="flex-1">
          <Title marginBottom={0}>Prioridades</Title>
        </div>
        <Private
          roles={[
            SsoRolesEnum.MASTER,
            SsoRolesEnum.SUPPORT,
            SsoRolesEnum.PHARMA_BENEFIT_MANAGER,
            SsoRolesEnum.RETAIL_PHARMA_BENEFIT_MANAGER,
          ]}
        >
          <div className="flex-shrink-0">
            <Button color="primary" onClick={onSubmit}>
              Salvar
            </Button>
          </div>
        </Private>
      </div>

      <BreadCrumb
        items={[
          { path: "/pbm/programs", label: "Prioridades" },
          {
            label: "CNPJ: " + establishmentIdentity,
          },
          { label: "Prioridade" },
        ]}
      />

      <div
        className="ag-theme-material"
        style={{
          height: "100vh",
          width: "100%",
          fontSize: "30px",
        }}
      >
        <AgGridReact
          rowData={createRowData()}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowDragManaged={true}
          animateRows={true}
          onRowDragEnd={onRowDragEnd}
          getRowHeight={getRowHeight}
        ></AgGridReact>
      </div>
    </>
  );
}
